import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ScopeItemNoteIn, ScopeItemRow } from "@/api/scope-activation";
import { bulkScopeItemNotes } from "@/api/scope-activation";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { diagnosticKeys } from "@/hooks/useDiagnostic";
import { formatDateTime } from "@/lib/datetime";
import { formatCount } from "@/lib/format";

/** O mesmo `max_length` do `ScopeItemNoteIn` no servidor. */
export const NOTE_MAX_LENGTH = 1000;

/** Texto em branco não é nota: o servidor o recusa, e apagar é mandar `null`. */
function notePayload(
  scopeItemId: string,
  text: string,
  showInReport: boolean,
): ScopeItemNoteIn {
  if (!text.trim()) return { scope_item_id: scopeItemId, curation_note: null };
  return { scope_item_id: scopeItemId, curation_note: text, show_in_report: showInReport };
}

/**
 * Editor da observação de curadoria de um scope item, no topo da linha
 * expandida do catálogo (#1344, S3).
 *
 * Grava na hora, fora do rascunho dos vereditos: a nota vive fora da aprovação
 * (pergunta 2, recomendação assumida), então continua editável com o escopo
 * aprovado. Quem monta o editor o remonta quando a nota salva muda, e o
 * rascunho local recomeça do que voltou do servidor.
 */
export function ScopeItemNoteEditor({
  row,
  projectId,
  executionId,
  disabled = false,
  onBusyChange,
}: {
  row: ScopeItemRow;
  projectId: string;
  executionId?: string;
  disabled?: boolean;
  onBusyChange?: (scopeItemId: string, busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const checkboxId = useId();

  // O que está gravado. Começa no que veio do servidor e passa ao que acabou de
  // ser gravado sem esperar a releitura do catálogo: até ela chegar (ou se ela
  // falhar), o mesmo texto não pode voltar a contar como alteração.
  const [saved, setSaved] = useState({
    text: row.curation_note ?? "",
    showInReport: row.show_in_report ?? true,
  });
  const [text, setText] = useState(saved.text);
  const [showInReport, setShowInReport] = useState(saved.showInReport);

  const save = useMutation({
    mutationKey: ["scope-item-note", projectId],
    mutationFn: (payload: ScopeItemNoteIn) => bulkScopeItemNotes(projectId, [payload]),
    onSuccess: (_result, payload) => {
      if (payload.curation_note == null) {
        // Apagada: uma nota nova a partir daqui nasce exibida no relatório.
        setSaved({ text: "", showInReport: true });
        setText((current) => (current.trim() ? current : ""));
      } else {
        setSaved({ text: payload.curation_note, showInReport: payload.show_in_report ?? true });
      }
      queryClient.invalidateQueries({ queryKey: ["scope-items", projectId, executionId] });
      // O snapshot do Diagnóstico fica 30 s em cache; o tile e o detalhe leem a nota dele.
      queryClient.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId) });
    },
  });

  const tooLong = text.length > NOTE_MAX_LENGTH;
  const changed = text !== saved.text || showInReport !== saved.showInReport;
  // Sem nota salva, texto em branco não tem o que gravar nem o que apagar.
  const nothingToDelete = !saved.text && !text.trim();
  const canSave = changed && !tooLong && !nothingToDelete && !save.isPending && !disabled;
  useEffect(() => {
    onBusyChange?.(row.scope_item_id, changed || save.isPending);
  }, [onBusyChange, row.scope_item_id, changed, save.isPending]);
  useEffect(() => () => onBusyChange?.(row.scope_item_id, false), [onBusyChange, row.scope_item_id]);

  const cancel = () => {
    setText(saved.text);
    setShowInReport(saved.showInReport);
    save.reset();
  };

  const author = row.note_updated_at
    ? [row.note_updated_by_name, formatDateTime(row.note_updated_at)].filter(Boolean).join(" · ")
    : null;

  return (
    <div
      className="flex flex-col gap-2 rounded-md border bg-background p-3"
      data-testid={`note-editor-${row.scope_item_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("Note")}
        </span>
        {author && <span className="text-[11px] text-muted-foreground">{author}</span>}
      </div>
      <Textarea
        aria-label={t("Curation note")}
        disabled={disabled || save.isPending}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="min-h-[64px] text-xs"
      />
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={`text-[11px] tabular-nums ${tooLong ? "font-semibold text-destructive" : "text-muted-foreground"}`}
        >
          {`${formatCount(text.length)} / ${formatCount(NOTE_MAX_LENGTH)}`}
        </span>
        <label
          htmlFor={checkboxId}
          className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground"
        >
          <input
            id={checkboxId}
            type="checkbox"
            className="h-3.5 w-3.5 accent-emerald-600"
            disabled={disabled || save.isPending}
            checked={showInReport}
            onChange={(e) => setShowInReport(e.target.checked)}
          />
          {t("Show in report")}
        </label>
        {save.isError && (
          <span className="text-[11px] text-destructive">{t("Could not save the note.")}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            disabled={disabled || !changed || save.isPending}
            onClick={cancel}
          >
            {t("Cancel")}
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs"
            disabled={!canSave}
            onClick={() => save.mutate(notePayload(row.scope_item_id, text, showInReport))}
          >
            {t("Save note")}
          </Button>
        </div>
      </div>
    </div>
  );
}
